/**
 * Regularly import this file on either server or client.
 * DO NOT PUT SENSITIVE INFORMATION LIKE ACCESS DATA HERE.
 * THIS FILE WILL BE BUNDLED TO THE CLIENT!
 */

export type AppEnvironment = 'development' | 'staging' | 'production'

interface ConfigOptions {
  $default?: any
  development?: any
  production?: any
  staging?: any
}

/**
 * Vite statically replaces `import.meta.env.SOME_KEY` at build time, so every
 * key has to be spelled out here — a computed lookup like
 * `import.meta.env[`VITE_${key}`]` compiles to `undefined` in a production
 * build and would silently fall back to the localhost defaults below.
 *
 * `REACT_APP_*` is still honoured (and `envPrefix` in vite.config.ts exposes
 * it) so the existing build-time environment keeps working unchanged; new
 * variables should use `VITE_*`.
 */
const ENV_OVERRIDES: Partial<Record<string, any>> = {
  ENV: import.meta.env.VITE_ENV || import.meta.env.REACT_APP_ENV,
  PORT: import.meta.env.VITE_PORT || import.meta.env.REACT_APP_PORT,
  PUBLIC_URL:
    import.meta.env.VITE_PUBLIC_URL || import.meta.env.REACT_APP_PUBLIC_URL,
  REST_API_URL:
    import.meta.env.VITE_REST_API_URL || import.meta.env.REACT_APP_REST_API_URL,
  GRAPHQL_API_URL:
    import.meta.env.VITE_GRAPHQL_API_URL ||
    import.meta.env.REACT_APP_GRAPHQL_API_URL,
  GRAPHQL_WS_URL:
    import.meta.env.VITE_GRAPHQL_WS_URL ||
    import.meta.env.REACT_APP_GRAPHQL_WS_URL,
  COOKIE_DOMAIN:
    import.meta.env.VITE_COOKIE_DOMAIN ||
    import.meta.env.REACT_APP_COOKIE_DOMAIN,
  PLAID_ENV:
    import.meta.env.VITE_PLAID_ENV || import.meta.env.REACT_APP_PLAID_ENV,
  PLAID_PUBLIC_KEY:
    import.meta.env.VITE_PLAID_PUBLIC_KEY ||
    import.meta.env.REACT_APP_PLAID_PUBLIC_KEY,
  FULL_STORY_ORG_ID:
    import.meta.env.VITE_FULL_STORY_ORG_ID ||
    import.meta.env.REACT_APP_FULL_STORY_ORG_ID,
  STRIPE_KEY:
    import.meta.env.VITE_STRIPE_KEY || import.meta.env.REACT_APP_STRIPE_KEY,
  FACEBOOK: import.meta.env.VITE_FACEBOOK || import.meta.env.REACT_APP_FACEBOOK,
  GOOGLE_ANALYTICS:
    import.meta.env.VITE_GOOGLE_ANALYTICS ||
    import.meta.env.REACT_APP_GOOGLE_ANALYTICS,
  GOOGLE_OPTIMIZE_EXPERIMENT_ID:
    import.meta.env.VITE_GOOGLE_OPTIMIZE_EXPERIMENT_ID ||
    import.meta.env.REACT_APP_GOOGLE_OPTIMIZE_EXPERIMENT_ID,
  SENTRY_DSN:
    import.meta.env.VITE_SENTRY_DSN || import.meta.env.REACT_APP_SENTRY_DSN,
  BACKEND_ROOT_URL:
    import.meta.env.VITE_BACKEND_ROOT_URL ||
    import.meta.env.REACT_APP_BACKEND_ROOT_URL,
  MY529_VERIFICATION_URL: import.meta.env.MY529_VERIFICATION_URL,
  HELP_URL: import.meta.env.VITE_HELP_URL || import.meta.env.REACT_APP_HELP_URL,
  WHAT_IS_529_URL:
    import.meta.env.VITE_WHAT_IS_529_URL ||
    import.meta.env.REACT_APP_WHAT_IS_529_URL,
  CLOUDINARY_CLOUD_NAME:
    import.meta.env.VITE_CLOUDINARY_CLOUD_NAME ||
    import.meta.env.REACT_APP_CLOUDINARY_CLOUD_NAME,
  CLOUDINARY_API_URL:
    import.meta.env.VITE_CLOUDINARY_API_URL ||
    import.meta.env.REACT_APP_CLOUDINARY_API_URL,
  BANK_OF_AMERICA_BROKER_ID:
    import.meta.env.VITE_BANK_OF_AMERICA_BROKER_ID ||
    import.meta.env.REACT_APP_BANK_OF_AMERICA_BROKER_ID
}

export class Config {
  PORT!: number
  PUBLIC_URL!: string
  API_URL!: string
  GRAPHQL_API_URL!: string
  REST_API_URL!: string
  GRAPHQL_WS_URL!: string
  COOKIE_DOMAIN!: string

  PLAID_ENV!: string
  PLAID_PUBLIC_KEY!: string

  FULL_STORY_ORG_ID!: string

  STRIPE_KEY!: string

  FACEBOOK!: {
    APP_ID: string
  }

  GOOGLE_ANALYTICS!: string

  GOOGLE_OPTIMIZE_EXPERIMENT_ID!: string

  SENTRY_DSN!: string

  CLOUDINARY_CLOUD_NAME!: string

  CLOUDINARY_API_URL!: string

  BACKEND_ROOT_URL!: string

  MY529_VERIFICATION_URL!: string

  HELP_URL!: string

  WHAT_IS_529_URL!: string

  BANK_OF_AMERICA_BROKER_ID!: number

  DEFAULT_SEO = {
    title: 'Backer',
    description:
      // tslint:disable-next-line
      'Easy way to sign up for 529 College Savings Plans and save more with gifts from family and friends, a 529 calculator, reviews of state plans, and information on contribution limits.',
    keywords:
      '529 plan, 529 calculator, 529 account, college, savings, tax-free, contribution limits, fund',
    openGraph: {
      type: 'website',
      locale: 'en_US',
      url: 'https://backer.com',
      title: 'Backer | The Social College Fund',
      description:
        'Start a tax-free 529 college savings plan in 5 minutes or connect your existing 529 plan. Easily invite family and friends and discover more ways to save for college, like our popular cashback rewards program.',
      image: 'https://backer.com/images/og-facebook.jpg',
      site_name: 'Backer',
      imageWidth: '1200',
      imageHeight: '1200'
    },
    twitter: {
      handle: '@backer529',
      cardType: 'summary',
      image: 'https://backer.com/images/og-twitter.jpg'
    }
  }

  private readonly localhostFallbacks: string[] = []

  constructor(public readonly env: AppEnvironment) {}

  init() {
    this.assign('PORT', {
      $default: 3082
    })

    this.assign('PUBLIC_URL', {
      $default: `http://localhost:${this.PORT}`,
      production: 'https://app.backer.com',
      staging: 'https://app.backer.link'
    })
    this.assign('REST_API_URL', {
      $default: 'http://localhost:4000/restapi',
      production: 'https://backer.com/restapi',
      staging: 'https://backer.link/restapi'
    })
    this.assign('GRAPHQL_API_URL', {
      $default: 'http://localhost:4000/api',
      production: 'https://backer.com/api',
      staging: 'https://backer.link/api'
    })
    this.assign('GRAPHQL_WS_URL', {
      $default: 'ws://localhost:4000/socket',
      production: 'wss://app.collegebacker.com/socket',
      staging: 'wss://backer.link/socket'
    })
    this.assign('COOKIE_DOMAIN', {
      $default: '',
      production: '.backer.com',
      staging: '.backer.link'
    })

    this.assign('PLAID_ENV', {
      $default: 'sandbox',
      production: 'production',
      staging: 'sandbox'
    })

    this.assign('PLAID_PUBLIC_KEY', {
      $default: '97ce77941181c53d3436d414e8ff99',
      production: '97ce77941181c53d3436d414e8ff99',
      staging: '97ce77941181c53d3436d414e8ff99'
    })

    this.assign('FULL_STORY_ORG_ID', {
      $default: '',
      production: 'o-19ZS7H-na1',
      staging: ''
    })

    this.assign('STRIPE_KEY', {
      $default:
        'pk_test_51HLHpGGz1i2SUAIhoNxZ6NFVr9dgwT5aOrbVIaLYsPYdeYQoXjoCjJ3fdPz9mzz0SMriyRIAgwG9COV8DVqizJS000m8IXKeD8',
      production: 'PRODUCTION STRIPE_KEY NOT SET',
      staging: 'STAGING STRIPE_KEY NOT SET'
    })

    this.assign('FACEBOOK', {
      $default: {
        APP_ID: '1743893495897419'
      },
      production: {
        APP_ID: '1743893495897419'
      },
      staging: {
        APP_ID: '1743902312563204'
      }
    })

    this.assign('GOOGLE_ANALYTICS', {
      $default: 'UA-146475365-1',
      production: 'UA-61619903-1',
      staging: 'UA-146475365-1'
    })

    this.assign('GOOGLE_OPTIMIZE_EXPERIMENT_ID', {
      $default: 'l4wH9c5WSvaZ_HsT9N0YcA',
      production: 'm87MdwsBSWWWMUA_ZpbwoA',
      staging: 'XnTalaG7SFGVyRW2t5O5Xw'
    })

    this.assign('SENTRY_DSN', {
      $default: '',
      staging:
        'https://f112670669d04cc79f1b3160e02ec7cf@o162127.ingest.sentry.io/6383106',
      production:
        'https://f112670669d04cc79f1b3160e02ec7cf@o162127.ingest.sentry.io/6383106'
    })

    this.assign('BACKEND_ROOT_URL', {
      $default: 'http://localhost:4000',
      production: 'https://backer.com',
      staging: 'https://backer.link'
    })

    this.assign('MY529_VERIFICATION_URL', {
      $default: 'http://localhost:4042/my529_verification'
    })

    this.assign('HELP_URL', {
      $default: 'https://help.backer.com',
      production: 'https://help.backer.com',
      staging: 'https://help.backer.com'
    })

    this.assign('WHAT_IS_529_URL', {
      $default: 'https://backer.com/529-college-investment-plans',
      production: 'https://backer.com/529-college-investment-plans',
      staging: 'https://backer.com/529-college-investment-plans'
    })

    this.assign('CLOUDINARY_CLOUD_NAME', {
      $default: 'dgfjsma5p',
      production: 'dtdmzmuqd',
      staging: 'dgfjsma5p'
    })

    this.assign('CLOUDINARY_API_URL', {
      $default: `https://api.cloudinary.com/v1_1/${this.CLOUDINARY_CLOUD_NAME}/image/upload`
    })

    this.assign('BANK_OF_AMERICA_BROKER_ID', {
      $default: 1,
      production: 1,
      staging: 1
    })

    this.reportLocalhostFallbacks()
  }

  private assign(key: keyof this, options: ConfigOptions): void {
    const override = ENV_OVERRIDES[String(key)]
    const value = override || options[this.env] || options.$default

    if (value === undefined) {
      throw new Error(`Config is missing for ${String(key)}`)
    }

    if (!override && /localhost|127\.0\.0\.1/.test(String(value))) {
      this.localhostFallbacks.push(String(key))
    }

    this[key] = value
  }

  /**
   * A missing build-time variable used to degrade into a localhost URL that
   * only surfaced as a broken deploy. Make it loud instead.
   */
  private reportLocalhostFallbacks(): void {
    if (this.env === 'development' || !this.localhostFallbacks.length) return

    console.error(
      `[config] ${this.env} build resolved ${this.localhostFallbacks.join(
        ', '
      )} to localhost defaults — the matching VITE_*/REACT_APP_* build variables are not set.`
    )
  }
}

/**
 * Previously read from `window.ENV`, which nothing ever set: every deploy ran
 * as `development`, mislabelling Sentry events and disabling the FullStory
 * identify call. Derived from the host now, with an explicit escape hatch.
 */
const detectEnvironment = (): AppEnvironment => {
  const explicit = ENV_OVERRIDES.ENV
  if (
    explicit === 'development' ||
    explicit === 'staging' ||
    explicit === 'production'
  ) {
    return explicit
  }

  if (typeof window === 'undefined') return 'development'

  const host = window.location.hostname
  if (/(^|\.)backer\.com$/.test(host)) return 'production'
  if (/(^|\.)backer\d*\.(link|studio)$/.test(host)) return 'staging'
  return 'development'
}

const config = new Config(detectEnvironment())
config.init()

export default config
